import { logger, createLogger } from '@/utils/logger'

// Create a namespaced logger
const fileLogger = createLogger('audio.service')

import { Howl, Howler } from 'howler'

/**
 * Service for playing audio
 */
export class AudioService {
  /**
   * The Howl instance for playing audio
   */
  private sound: Howl | null = null

  /**
   * The current URL of the audio file being played
   */
  private currentUrl: string | null = null
  
  /**
   * The listeners for the audio service
   */
  private listeners: { [key: string]: Function[] } = {}

  constructor() {
    // Howler global settings
    Howler.autoUnlock = true
    Howler.html5PoolSize = 10
  }

  /**
   * Play an audio file
   * @param url - The URL of the audio file to play
   * @returns A promise that resolves when the audio starts playing
   */
  async play(url: string) {
    if (!this.sound || this.currentUrl !== url) {
      // Unload previous sound
      this.sound?.unload()
      this.currentUrl = url
      
      // Create new sound
      this.sound = new Howl({
        src: [url],
        html5: true, // Force HTML5 Audio for streaming
        preload: true,
        volume: 1.0,
        onend: () => {
          this.emit('ended')
        },
        onload: () => {
          this.emit('loaded')
        },
        onloaderror: (id, error) => {
          fileLogger.error('Load error:', error)
        },
        onplayerror: (id, error) => {
          fileLogger.error('Play error:', error)
        }
      })
    }

    return this.sound?.play()
  }

  /**
   * Pause the currently playing audio
   */
  pause() {
    this.sound?.pause()
  }

  /**
   * Stop the currently playing audio
   */
  stop() {
    this.sound?.stop()
  }

  /**
   * Set the volume of the currently playing audio
   * @param value - The volume value to set (0 to 1)
   */
  setVolume(value: number) {
    this.sound?.volume(value)
  }

  /**
   * Seek to a specific time in the audio
   * @param time - The time to seek to in seconds
   */
  seek(time: number) {
    this.sound?.seek(time)
  }

  /**
   * Get the duration of the currently playing audio
   * @returns The duration of the audio in seconds
   */
  getDuration(): number {
    return this.sound?.duration() ?? 0
  }

  /**
   * Get the current time of the currently playing audio
   * @returns The current time of the audio in seconds
   */
  getCurrentTime(): number {
    return this.sound?.seek() as number ?? 0
  }

  /**
   * Add an event listener for a specific event
   * @param event - The event to listen for
   * @param callback - The callback function to call when the event occurs
   */
  on(event: string, callback: Function) {
    if (!this.listeners[event]) {
      this.listeners[event] = []
    }
    this.listeners[event].push(callback)
  }

  /**
   * Remove an event listener for a specific event
   * @param event - The event to remove the listener from
   * @param callback - The callback function to remove
   */
  off(event: string, callback: Function) {
    if (this.listeners[event]) {
      this.listeners[event] = this.listeners[event].filter(cb => cb !== callback)
    }
  }

  /**
   * Emit an event with optional arguments
   * @param event - The event to emit
   * @param args - Optional arguments to pass to the event listeners
   */
  private emit(event: string, ...args: any[]) {
    if (this.listeners[event]) {
      this.listeners[event].forEach(callback => callback(...args))
    }
  }
} 